import { CustomTooltip } from "@shared/components/CustomTooltip";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Bell, Users } from "lucide-react";

interface MonitoringTeamSectionProps {
  data: any;
}

export const MonitoringTeamSection = ({ data }: MonitoringTeamSectionProps) => {
  const getMetricColor = (
    value: number,
    goodThreshold: number,
    poorThreshold: number,
    reverse = false
  ) => {
    if (reverse) {
      if (value <= goodThreshold) return "text-status-good";
      if (value <= poorThreshold) return "text-status-fair";
      return "text-status-poor";
    } else {
      if (value >= goodThreshold) return "text-status-good";
      if (value >= poorThreshold) return "text-status-fair";
      return "text-status-poor";
    }
  };

  const getRatingColor = (rating: string) => {
    switch (rating) {
      case "excellent":
        return "text-status-good";
      case "good":
        return "text-status-fair";
      case "needs-improvement":
        return "text-status-poor";
      default:
        return "text-muted-foreground";
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Anomalies ({data?.realTimeAnomalies?.length})
            </CardTitle>
            <CustomTooltip message="Current system anomalies detected through real-time monitoring, categorized by severity level for immediate action." />
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-3">
            {data?.realTimeAnomalies && data.realTimeAnomalies.length > 0 ? (
              data.realTimeAnomalies.map((alert, index) => (
                <div
                  key={index}
                  className="flex justify-between items-start p-3 rounded-lg border"
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm">{alert.assetName}</p>
                    <p className="text-xs text-muted-foreground">
                      {alert.issue}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Detected: {alert.detected}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge
                      variant={
                        alert.priority === "high"
                          ? "destructive"
                          : alert.priority === "medium"
                          ? "outline"
                          : "secondary"
                      }
                      className="mb-1 text-xs"
                    >
                      {alert.priority}
                    </Badge>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-sm text-muted-foreground">
                No anomalies detected.
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Technician Performance
            </CardTitle>
            <CustomTooltip message="Individual technician performance metrics including completion rates, average time per task, and overall performance rating." />
          </div>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {data?.technicianPerformance &&
            data.technicianPerformance.length > 0 ? (
              data.technicianPerformance.map((tech, index) => (
                <div key={index} className="p-3 rounded-lg border">
                  <p className="font-medium text-sm mb-2">{tech.name}</p>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span>Completion:</span>
                      <span
                        className={getMetricColor(tech.completionRate, 90, 80)}
                      >
                        {tech.completionRate}%
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Avg Time:</span>
                      <span>{tech.avgTime}h</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Rating:</span>
                      <span className={getRatingColor(tech.rating)}>
                        {tech.rating.replace("-", " ")}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-sm text-muted-foreground col-span-full">
                No technician performance data available.
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
